import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { startSurvey } from '@intercom/messenger-js-sdk';
import { getEnvVar } from '@studio/environment';
import { startStreams, stopStreams } from '@shoflo/studio-redux/actions';
import { useStreamSLI } from '@studio/metrics';
import { sendToDatadog } from '@shoflo/polymer';
import { useLocation, useRouteMatch } from 'react-router-dom';
import {
  RESET_BROADCAST_STREAM_START_FAILED_STATUS,
  RESET_BROADCAST_STREAM_STOP_FAILED_STATUS,
} from '@shoflo/studio-redux/actions/types';
import {
  endStreamStartMetric,
  endStreamStopMetric,
} from 'utils/metric/streamSLIHelper';
import {
  checkStreamStartFailedStatus,
  checkStreamStopFailedStatus,
} from './utils';
import { BROADCAST_TYPE } from 'constants/SearchParams';
import {
  isEndpointActive,
  isEndpointStarting,
  isEndpointStopping,
} from '../useStreamButtonState/utils';

export function useStreamActionUtils() {
  const query = new URLSearchParams(useLocation().search);
  const broadcastType = query.get(BROADCAST_TYPE) ?? '';
  const broadcast = useSelector(({ broadcast }) => broadcast);
  const streamEndpoints = useSelector(
    ({ streamEndpoints }) => streamEndpoints,
    shallowEqual
  );

  let endOn;
  if (broadcast) {
    endOn = broadcast.endOn;
  }

  const {
    startStreamStartSLI,
    startStreamEndSLI,
    stopStreamStartSLI,
    stopStreamEndSLI,
  } = useStreamSLI();
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const [isStreamStartSLIActive, setIsStreamStartSLIActive] = useState(false);
  const [isStreamStopSLIActive, setIsStreamStopSLIActive] = useState(false);

  useEffect(() => {
    if (isStreamStartSLIActive) {
      endStreamStartMetric(
        streamEndpoints,
        startStreamEndSLI,
        setIsStreamStartSLIActive
      );
    }

    if (isStreamStopSLIActive) {
      endStreamStopMetric(
        streamEndpoints,
        stopStreamEndSLI,
        setIsStreamStopSLIActive
      );
    }
  }, [
    startStreamEndSLI,
    stopStreamEndSLI,
    isStreamStartSLIActive,
    isStreamStopSLIActive,
    streamEndpoints,
  ]);

  const getInactiveEndpoints = useCallback(() => {
    return streamEndpoints?.filter(
      (endpoint) =>
        !isEndpointActive(endpoint) &&
        !isEndpointStopping(endpoint) &&
        !isEndpointStarting(endpoint)
    );
  }, [streamEndpoints]);

  const getActiveEndpoints = useCallback(() => {
    return streamEndpoints?.filter(
      (endpoint) =>
        isEndpointActive(endpoint) ||
        !isEndpointStopping(endpoint) ||
        !isEndpointStarting(endpoint)
    );
  }, [streamEndpoints]);

  const getTransitioningEndpoints = useCallback(() => {
    return streamEndpoints?.filter(
      (endpoint) => isEndpointStopping(endpoint) || isEndpointStarting(endpoint)
    );
  }, [streamEndpoints]);

  const formattedStreams = useMemo(() => {
    return streamEndpoints?.map(({ _id, title, startedAt, isStreaming }) => {
      return {
        _id,
        title,
        startedAt,
        isStreaming,
      };
    });
  }, [streamEndpoints]);

  // TODO: Currently we are passing the first stream in the activeStreamIds to the SLI.
  // When multicast is implemented, we need to start all stream SLIs, similar to pro.
  // ref: https://github.com/shoflo-internal/studio/blob/WEBINAR-34571/projects/app.studio.director/src/utils/broadcast/handleEndpointOperations.js#L48
  const onStartStreams = useCallback(() => {
    sendToDatadog(
      'studio-stream',
      {},
      'Studio Stream Start - Beginning Start Stream Flow'
    );
    const inActiveStreams = getInactiveEndpoints();
    const inActiveStreamsIds = inActiveStreams?.map((stream) => {
      return stream._id;
    });

    if (inActiveStreamsIds?.length) {
      sendToDatadog(
        'studio-stream',
        {},
        'Studio Stream Start - Active Streams found'
      );

      if (checkStreamStartFailedStatus(inActiveStreams)) {
        sendToDatadog(
          'studio-stream',
          {},
          'Studio Stream Start - Start failed detected in inactive stream list.'
        );
        dispatch({
          type: RESET_BROADCAST_STREAM_START_FAILED_STATUS,
          payload: { endpoints: inActiveStreams },
        });
      }
      setIsStreamStartSLIActive(true);
      startStreamStartSLI({
        streamId: inActiveStreamsIds[0],
        pathName: match.path,
        url: match.url,
      });

      return dispatch(startStreams(inActiveStreamsIds));
    }
  }, [
    getInactiveEndpoints,
    dispatch,
    startStreamStartSLI,
    match.path,
    match.url,
    endOn,
    broadcastType,
  ]);

  const onStopStreams = useCallback(() => {
    sendToDatadog(
      'studio-stream',
      {},
      'Studio Stream Stop - Beginning Stop Stream Flow'
    );
    const activeStreams = getActiveEndpoints();
    const activeStreamsIds = activeStreams?.map((stream) => stream._id);

    if (activeStreamsIds?.length) {
      sendToDatadog(
        'studio-stream',
        {},
        'Studio Stream Stop - Active Streams found'
      );

      if (checkStreamStopFailedStatus(activeStreams)) {
        sendToDatadog(
          'studio-stream',
          {},
          'Studio Stream Stop - Stop failed detected in inactive stream list.'
        );
        dispatch({
          type: RESET_BROADCAST_STREAM_STOP_FAILED_STATUS,
          payload: { endpoints: activeStreams },
        });
      }

      // render stop stream survey
      startSurvey(getEnvVar('REACT_APP_INTERCOM_STOP_STREAM_SURVEY_ID'));

      setIsStreamStopSLIActive(true);
      stopStreamStartSLI({
        streamId: activeStreamsIds[0],
        pathName: match.path,
        url: match.url,
      });

      return dispatch(stopStreams(activeStreamsIds));
    }
  }, [dispatch, getActiveEndpoints, match.path, match.url, stopStreamStartSLI]);

  const onQuitStreams = useCallback(() => {
    sendToDatadog(
      'studio-stream',
      {},
      'Studio Stream Quit - Beginning Quit Stream Flow'
    );
    const transitioningEndpoints = getTransitioningEndpoints();
    const transitioningStreamIds = transitioningEndpoints?.map(
      (stream) => stream._id
    );

    if (transitioningStreamIds?.length) {
      return dispatch(stopStreams(transitioningStreamIds));
    }
  }, [dispatch, getTransitioningEndpoints]);

  return {
    formattedStreams,
    onStartStreams,
    onStopStreams,
    onQuitStreams,
  };
}

export default useStreamActionUtils;
